import { keyAgentExecutionSessionMapping } from "@/components/ai/session/session-model";

export class SessionPlan {
    id: string;
    title: string;
    steps: SessionPlanStep[];

    constructor(id: string, title: string, steps?: SessionPlanStep[]) {
        this.id = id;
        this.title = title;
        this.steps = steps ?? [];
    }

    addStep(step: SessionPlanStep): void {
        this.steps.push(step);
    }

    toXML(): string {
        const stepsXml = this.steps
            .map(
                (step) => `
        <step id="${step.id}" agentId="${step.spec.agentId}" title="${step.spec.title}">
            <prompt>${step.spec.prompt}</prompt>
        </step>`,
            )
            .join("");

        return `<?xml version="1.0" encoding="UTF-8"?>
<plan id="${this.id}" title="${this.title}">
    <steps>${stepsXml}
    </steps>
</plan>`;
    }

    static fromXML(xml: string): SessionPlan {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, "text/xml");

        const planElement = xmlDoc.querySelector("plan");
        if (!planElement) {
            throw new Error("Invalid XML: missing plan element");
        }

        const planId = planElement.getAttribute("id") || "";
        const planTitle = planElement.getAttribute("title") || "";

        const plan = new SessionPlan(planId, planTitle);

        const stepElements = planElement.querySelectorAll("step");
        stepElements.forEach((stepElement) => {
            const stepId = stepElement.getAttribute("id") || "";
            const agentId = stepElement.getAttribute("agentId") || "";
            const title = stepElement.getAttribute("title") || "";

            const promptElement = stepElement.querySelector("prompt");
            const prompt = promptElement ? promptElement.textContent || "" : "";

            const stepSpec: SessionPlanStepSpec = {
                agentId,
                title,
                prompt: trim(prompt),
            };

            const step = new SessionPlanStep(stepId, stepSpec);
            plan.addStep(step);
        });

        return plan;
    }

    static exampleXML(): string {
        return `
<?xml version="1.0" encoding="UTF-8"?>
<plan id="example-id" title="Example plan">
    <steps>
        <step id="step-1" agentId="agent-1" title="Step 1">
            <prompt>This is a prompt</prompt>
        </step>
        <step id="step-2" agentId="agent-2" title="Step 2">
            <prompt>This is another prompt</prompt>
        </step>
    </steps>
</plan>`.trim();
    }
}

export class SessionPlanStep {
    id: string;
    spec: SessionPlanStepSpec;

    constructor(id: string, spec: SessionPlanStepSpec) {
        this.id = id;
        this.spec = spec;
    }

    delete(sessionId: string): void {
        window.localStorage.removeItem(this.key(sessionId));
    }

    setAgentExecutionId(sessionId: string, agentExecutionId: string): void {
        window.localStorage.setItem(keyAgentExecutionSessionMapping(agentExecutionId), sessionId);
        window.localStorage.setItem(this.key(sessionId), agentExecutionId);
    }

    getAgentExecutionId(sessionId: string): string | undefined {
        return window.localStorage.getItem(this.key(sessionId)) || undefined;
    }

    getStatus(sessionId: string): SessionPlanStepStatus {
        // TODO(mads): Missing support for running / completed / failed
        const agentExecutionId = this.getAgentExecutionId(sessionId);
        if (!agentExecutionId) {
            return { phase: "pending" };
        } else {
            return { phase: "running" };
        }
    }

    private key(sessionId: string): string {
        return `session-${sessionId}-plan-step-${this.id}`;
    }
}

export type SessionPlanStepSpec = {
    agentId: string;
    title: string;
    prompt: string;
};

export type SessionPlanStepStatus = {
    phase: "pending" | "running" | "completed" | "failed";
};

function trim(prompt: string): string {
    const trimmed = prompt.replace(/^\n+/, "");

    const leading = /^[\s]+/.exec(trimmed)?.[0];
    if (!leading) {
        return prompt;
    }

    console.log(`leading: |${leading}|`, leading, prompt);

    return prompt
        .split("\n")
        .map((line) => line.replace(leading, ""))
        .join("\n");
}
